/**
 * Where the app stops laying a logo out beside the school name and gives it the whole row.
 *
 * Mirrors `WIDE_STUDIO_LOGO_RATIO` in the mobile app. The two must agree, because this is what the
 * preview on this page promises and that is what the app actually draws.
 */
export const WIDE_LOGO_RATIO = 2.5;

export interface ILogoDimensions {
  width: number;
  height: number;
}

/**
 * The pixel dimensions of a file the user just picked.
 *
 * Measured here rather than on the API because the browser already has the bytes and can do it for
 * nothing, where the API would need image decoding in a Lambda. The app needs them to choose its
 * header layout before the image loads — without them it has to measure at render, and the header
 * visibly reflows.
 *
 * Resolves `undefined` rather than rejecting when the file will not decode: a logo that cannot be
 * measured should still upload, and the app falls back to measuring it itself.
 */
export const measureImageDimensions = (file: File): Promise<ILogoDimensions | undefined> =>
  new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    const done = (result?: ILogoDimensions) => {
      URL.revokeObjectURL(objectUrl);
      resolve(result);
    };

    image.onload = () => done(
      image.naturalWidth && image.naturalHeight
        ? { width: image.naturalWidth, height: image.naturalHeight }
        : undefined
    );
    image.onerror = () => done(undefined);
    image.src = objectUrl;
  });

/** Whether a logo takes the app header's whole row. Undefined when we have nothing to judge by. */
export const isWideLogo = (dimensions?: ILogoDimensions): boolean | undefined =>
  dimensions?.width && dimensions?.height
    ? dimensions.width / dimensions.height > WIDE_LOGO_RATIO
    : undefined;
